import { useMemo } from 'react';

import {
  ChakraBox,
  MultiSelectOption,
  Tag,
  TagProps,
  Text,
  UniversalSelect,
} from '@equip.health/ui';

import { EMPTY_PLACEHOLDER } from '~/components/maud/patients/treatmentEpisode/Types';

export interface UniversalSelectTagViewProps {
  id: string;
  isClearable?: boolean;
  isReadOnly: boolean;
  onChange: (newValue: string | null) => void;
  options: TagProps[];
  value: string;
}

interface ReadOnlyContentProps {
  id: string;
  tagProps?: TagProps;
  value?: string;
}

const ReadOnlyContent = ({ tagProps, id, value }: ReadOnlyContentProps) =>
  tagProps ? (
    <Tag {...tagProps} />
  ) : (
    <Text color="neutral.tertiary" id={id}>
      {value || EMPTY_PLACEHOLDER}
    </Text>
  );

const UniversalSelectTagView = ({
  id,
  isClearable = true,
  isReadOnly,
  options,
  onChange,
  value,
}: UniversalSelectTagViewProps) => {
  const currentTag: TagProps | undefined = useMemo(
    () => options.find((s) => s.value === value),
    [value, options],
  );

  const dropdownOptions: MultiSelectOption[] = useMemo<
    MultiSelectOption[]
  >(() => {
    return options.map((status) => {
      return {
        label: (
          <Tag color={status.color} id={`${id}__tag`} value={status.value} />
        ),
        searchData: status.value.toLowerCase(),
        value: status.value,
      };
    });
  }, [options, id]);

  const dropdownOption = useMemo<MultiSelectOption | undefined>(() => {
    return (dropdownOptions ?? []).find((o) => o.value === value);
  }, [dropdownOptions, value]);

  return (
    <ChakraBox>
      {isReadOnly ? (
        <ReadOnlyContent id={id} tagProps={currentTag} value={value} />
      ) : (
        <UniversalSelect
          id={`${id}__edit`}
          isClearable={isClearable}
          isSingleSelect
          onChange={(v) => onChange(v?.value)}
          options={dropdownOptions}
          value={dropdownOption}
        />
      )}
    </ChakraBox>
  );
};

export default UniversalSelectTagView;
